import { useCallback, useEffect, useState } from 'react';

import {
  getNextVisibleNodeIndex,
  getPreviousVisibleNodeIndex,
} from './helpers';
import { TREE_ACTIONS } from '../consts';
import { BaseAction, DefaultInputTreeNode, IdType, TreeState } from '../types';

const useScrollIntoView = <InputTreeNode extends DefaultInputTreeNode>({
  getNodeById,
}: {
  getNodeById: (id: IdType) => Element | null | undefined;
}) => {
  const [idToHighlight, setIdToHighlight] = useState<IdType | undefined>(
    undefined,
  );

  const scrollIntoViewReducer = useCallback(
    (
      treeState: TreeState<InputTreeNode>,
      action: BaseAction<InputTreeNode>,
    ) => {
      const { orderedIds, normalizedTree } = treeState;

      switch (action.type) {
        case TREE_ACTIONS.moveDown:
          const nextDownIndex = action.payload.id
            ? getNextVisibleNodeIndex({
                currentNodeId: action.payload.id,
                orderedIds,
                normalizedTree,
              })
            : 0;

          if (nextDownIndex < treeState.orderedIds.length) {
            setIdToHighlight(orderedIds[nextDownIndex]);
          }
          break;
        case TREE_ACTIONS.moveUp:
          const nextUpIndex = action.payload.id
            ? getPreviousVisibleNodeIndex({
                currentNodeId: action.payload.id,
                orderedIds: treeState.orderedIds,
                normalizedTree,
              })
            : treeState.orderedIds.length - 1;

          if (nextUpIndex < treeState.orderedIds.length) {
            setIdToHighlight(orderedIds[nextUpIndex]);
          }
          break;

        case TREE_ACTIONS.moveNode:
          setIdToHighlight(action.payload.targetId);
          break;

        default:
          break;
      }

      return treeState;
    },
    [],
  );

  const scrollElementIntoView = useCallback(
    (highlightedId?: IdType) => {
      // We only want to scroll elements into view when navigating via keyboard
      if (highlightedId) {
        const element = getNodeById(highlightedId);

        if (element) {
          element.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        }
      }
    },
    [getNodeById],
  );

  useEffect(() => {
    scrollElementIntoView(idToHighlight);
  }, [scrollElementIntoView, idToHighlight]);

  return {
    scrollIntoViewReducer,
  };
};

export default useScrollIntoView;
